import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { accessibleViewIsShown, accessibleViewCurrentProviderId } from 'vscode/vscode/vs/workbench/contrib/accessibility/browser/accessibilityConfiguration';
import { isDetachedTerminalInstance } from 'vscode/vscode/vs/workbench/contrib/terminal/browser/terminal';
import { registerActiveInstanceAction } from '../../../terminal/browser/terminalActions.js';
import { registerTerminalContribution } from '../../../terminal/browser/terminalExtensions.js';
import { isTerminalProcessManager } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminal';
import { TerminalContextKeys } from 'vscode/vscode/vs/workbench/contrib/terminal/common/terminalContextKey';
import { terminalStrings } from '../../../terminal/common/terminalStrings.js';
import { ITerminalLinkProviderService } from 'vscode/vscode/vs/workbench/contrib/terminalContrib/links/browser/links.service';
import { TerminalLinkManager } from './terminalLinkManager.js';
import { TerminalLinkQuickpick } from './terminalLinkQuickpick.js';
import { TerminalLinkResolver } from './terminalLinkResolver.js';

var TerminalLinkContribution_1;
const _moduleId = "vs/workbench/contrib/terminalContrib/links/browser/terminal.links.contribution";
let TerminalLinkContribution = class TerminalLinkContribution extends DisposableStore {
    static { TerminalLinkContribution_1 = this; }
    static { this.ID = 'terminal.link'; }
    static get(instance) {
        return instance.getContribution(TerminalLinkContribution_1.ID);
    }
    constructor(_instance, _processManager, _widgetManager, _instantiationService, _terminalLinkProviderService) {
        super();
        this._instance = _instance;
        this._processManager = _processManager;
        this._widgetManager = _widgetManager;
        this._instantiationService = _instantiationService;
        this._terminalLinkProviderService = _terminalLinkProviderService;
        this._linkResolver = this._instantiationService.createInstance(TerminalLinkResolver);
    }
    xtermReady(xterm) {
        const linkManager = this._linkManager = this.add(this._instantiationService.createInstance(TerminalLinkManager, xterm.raw, this._processManager, this._instance.capabilities, this._linkResolver));
        if (isTerminalProcessManager(this._processManager)) {
            const disposable = linkManager.add(Event.once(this._processManager.onProcessReady)(() => {
                linkManager.setWidgetManager(this._widgetManager);
                this.delete(disposable);
            }));
        }
        else {
            linkManager.setWidgetManager(this._widgetManager);
        }
        if (!isDetachedTerminalInstance(this._instance)) {
            for (const linkProvider of this._terminalLinkProviderService.linkProviders) {
                linkManager.externalProvideLinksCb = linkProvider.provideLinks.bind(linkProvider, this._instance);
            }
            linkManager.add(this._terminalLinkProviderService.onDidAddLinkProvider(e => {
                linkManager.externalProvideLinksCb = e.provideLinks.bind(e, this._instance);
            }));
        }
        linkManager.add(this._terminalLinkProviderService.onDidRemoveLinkProvider(() => linkManager.externalProvideLinksCb = undefined));
    }
    async showLinkQuickpick(extended) {
        if (!this._terminalLinkQuickpick) {
            this._terminalLinkQuickpick = this.add(this._instantiationService.createInstance(TerminalLinkQuickpick));
            this._terminalLinkQuickpick.onDidRequestMoreLinks(() => {
                this.showLinkQuickpick(true);
            });
        }
        const links = await this._getLinks();
        return await this._terminalLinkQuickpick.show(this._instance, links);
    }
    async _getLinks() {
        if (!this._linkManager) {
            throw ( (new Error('terminal links are not ready, cannot generate link quick pick')));
        }
        return this._linkManager.getLinks();
    }
    async openRecentLink(type) {
        if (!this._linkManager) {
            throw ( (new Error('terminal links are not ready, cannot open a link')));
        }
        this._linkManager.openRecentLink(type);
    }
};
TerminalLinkContribution = TerminalLinkContribution_1 = ( (__decorate([
    ( (__param(3, IInstantiationService))),
    ( (__param(4, ITerminalLinkProviderService)))
], TerminalLinkContribution)));
registerTerminalContribution(TerminalLinkContribution.ID, TerminalLinkContribution, true);
const category = terminalStrings.actionCategory;
registerActiveInstanceAction({
    id: "workbench.action.terminal.openDetectedLink" ,
    title: ( localize2WithPath(_moduleId, 0, 'Open Detected Link...')),
    f1: true,
    category,
    precondition: TerminalContextKeys.terminalHasBeenCreated,
    keybinding: [{
            primary: 2048  | 1024  | 45 ,
            weight: 200  + 1,
            when: TerminalContextKeys.focus
        }, {
            primary: 2048  | 1024  | 37 ,
            weight: 200  + 1,
            when: ( (ContextKeyExpr.and(accessibleViewIsShown,  (ContextKeyExpr.equals(
                accessibleViewCurrentProviderId.key,
                "terminal"
            )))))
        },
    ],
    run: (activeInstance) => TerminalLinkContribution.get(activeInstance)?.showLinkQuickpick()
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.openUrlLink" ,
    title: ( localize2WithPath(_moduleId, 1, 'Open Last URL Link')),
    metadata: {
        description: ( localize2WithPath(_moduleId, 2, 'Opens the last detected URL/URI link in the terminal'))
    },
    f1: true,
    category,
    precondition: TerminalContextKeys.terminalHasBeenCreated,
    run: (activeInstance) => TerminalLinkContribution.get(activeInstance)?.openRecentLink('url')
});
registerActiveInstanceAction({
    id: "workbench.action.terminal.openFileLink" ,
    title: ( localize2WithPath(_moduleId, 3, 'Open Last Local File Link')),
    f1: true,
    category,
    precondition: TerminalContextKeys.terminalHasBeenCreated,
    run: (activeInstance) => TerminalLinkContribution.get(activeInstance)?.openRecentLink('localFile')
});
